import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const STORAGE_KEY = 'knowindia_admin_onboarding_done';

/**
 * Check if onboarding has been completed
 * @returns {boolean}
 */
export const isOnboardingComplete = () => {
  return localStorage.getItem(STORAGE_KEY) === 'true';
};

/**
 * Mark onboarding as complete
 */
export const markOnboardingComplete = () => {
  localStorage.setItem(STORAGE_KEY, 'true');
};

/**
 * Reset onboarding status
 */
export const resetOnboarding = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Create and configure the Driver.js instance
 * @returns {Object} Driver instance
 */
export const createTourDriver = () => {
  const driverObj = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    animate: true,
    smoothScroll: true,
    allowClose: true,
    overlayClickBehavior: 'close',
    stagePadding: 12,
    stageRadius: 12,
    popoverClass: 'knowindia-tour-popover',
    progressText: '{{current}} of {{total}}',
    nextBtnText: 'Next →',
    prevBtnText: '← Back',
    doneBtnText: 'Done ✓',
    onDestroyStarted: () => {
      markOnboardingComplete();
      driverObj.destroy();
    },
    steps: [
      {
        element: '[data-tour="sidebar"]',
        popover: {
          title: '👋 Welcome to Know India Admin',
          description: 'This is your navigation hub. Access all admin features from here - Places, Categories, Journals, Feedback, and Analytics.',
          side: 'right',
          align: 'start'
        }
      },
      {
        element: '[data-tour="nav-places"]',
        popover: {
          title: '📍 Manage Places',
          description: 'Add, edit, and organize all tourist places across India. This is where you\'ll spend most of your time.',
          side: 'right',
          align: 'center'
        }
      },
      {
        element: '[data-tour="nav-categories"]',
        popover: {
          title: '🏷️ Categories',
          description: 'Organize places into categories like Heritage Sites, Temples, Nature Parks, and more.',
          side: 'right',
          align: 'center'
        }
      },
      {
        element: '[data-tour="nav-journals"]',
        popover: {
          title: '📖 Journal Moderation',
          description: 'Review and moderate user-submitted journals. Approve quality content and maintain platform standards.',
          side: 'right',
          align: 'center'
        }
      },
      {
        element: '[data-tour="nav-feedback"]',
        popover: {
          title: '💬 User Feedback',
          description: 'Read and respond to user feedback. Track what users love and areas for improvement.',
          side: 'right',
          align: 'center'
        }
      },
      {
        element: '[data-tour="nav-analytics"]',
        popover: {
          title: '📊 Analytics Dashboard',
          description: 'Get insights into platform health - content stats, moderation status, and feedback metrics.',
          side: 'right',
          align: 'center'
        }
      },
      {
        element: '[data-tour="user-menu"]',
        popover: {
          title: '👤 Your Account',
          description: 'Access your profile and logout from here. You can restart this tour anytime from the Help button.',
          side: 'bottom',
          align: 'end'
        }
      },
      {
        popover: {
          title: '🎉 You\'re All Set!',
          description: 'You now know the basics of the Know India Admin Dashboard. Start by adding a new place or reviewing pending journals. Happy managing!',
          side: 'center',
          align: 'center'
        }
      }
    ]
  });

  return driverObj;
};

/**
 * Start the onboarding tour
 * @param {boolean} force - Force start even if already completed
 */
export const startTour = (force = false) => {
  if (!force && isOnboardingComplete()) {
    return;
  }

  // Small delay to ensure DOM is ready
  setTimeout(() => {
    const driverObj = createTourDriver();
    driverObj.drive();
  }, 500);
};

const onboardingUtils = {
  isOnboardingComplete,
  markOnboardingComplete,
  resetOnboarding,
  startTour,
  createTourDriver
};

export default onboardingUtils;

